import { CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  type ForwardedRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import { FullTitlePreviewStyle } from '@/components/Preview/FullTitlePreview';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import { COMPANY_UNKNOWN, CUSTOMER_ANONYMOUS } from '@/configs/reserved-word';
import TicketLabel from '@/features/Commons/Ticket/TicketLabel';
import useColor from '@/hooks/domain/useColor';
import useUserRole from '@/hooks/domain/useUserRole';
import useFixed from '@/hooks/pure/useFixed';
import useFeedbackListStore from '@/stores/Feedback/FeedbackListStore';
import { EllipsisDiv } from '@/styles/style';
import { DEFAULT_HIGHLIGHT_TARGET } from '@/utils/util';

type Props = StateProps & {
  feedback: FeedbackSimpleListItemT;
  onClick?: (feedbackAlias: string) => void;
  ellipsisTitleOnLine?: number;
};

type StateProps = {
  selected?: boolean;
  labeled?: 'Selected' | 'Linked';
  deleted?: boolean;
};

const FeedbackShortItem = (
  {
    feedback,
    selected,
    labeled,
    deleted,
    onClick,
    ellipsisTitleOnLine = 1,
  }: Props,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const color = useColor();
  const { isEditor } = useUserRole();
  const feedbackMap = useFeedbackListStore((state) => state.feedbackMap);
  const titleRef = useRef<HTMLDivElement>(null);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);
  const [displayFullTitle, setDisplayFullTitle] = useState(false);

  const fixedPos = useFixed(titleRef);

  useEffect(() => {
    if (!(titleRef && titleRef.current)) return;
    setIsEllipsisActive(
      titleRef.current.offsetWidth < titleRef.current.scrollWidth,
    );
  }, [titleRef]);

  const compositedFeedback = useMemo(
    () => ({
      ...feedback,
      ...(feedbackMap.get(feedback.alias) || {}),
    }),
    [feedback, feedbackMap],
  );

  const displayingTitle = useMemo(() => {
    return (
      <Typo
        type={'body2'}
        color={color.gray700}
        className={DEFAULT_HIGHLIGHT_TARGET}
        multilineEllipsis={ellipsisTitleOnLine}
      >
        {feedback?.title || 'Untitled'}
      </Typo>
    );
  }, [color.gray700, feedback?.title, ellipsisTitleOnLine]);

  return (
    <Wrapper ref={ref}>
      {!compositedFeedback ? (
        <Style style={{ height: 40 }} a="center" j="center">
          <Spinner />
        </Style>
      ) : (
        <Style
          g={8}
          selected={selected}
          labeled={labeled}
          deleted={deleted}
          j="center"
          a="stretch"
          onClick={() => onClick?.(compositedFeedback.alias)}
        >
          {labeled && (
            <LabelStyle style={{ color: color.primary100 }}>
              <CheckOutlined />
              <Typo type="label2">{labeled}</Typo>
            </LabelStyle>
          )}
          {deleted && (
            <LabelStyle>
              <DeleteOutlined />
              <Typo type="label2">Deleted</Typo>
            </LabelStyle>
          )}
          <Flex g="8px" a="center">
            <TicketLabel type="feedback">{compositedFeedback.tid}</TicketLabel>
            <Flex g="8px">
              <EllipsisDiv style={{ maxWidth: 120 }}>
                <MainText>
                  {compositedFeedback?.company_info?.name || COMPANY_UNKNOWN}
                </MainText>
              </EllipsisDiv>
              {isEditor && (
                <EllipsisDiv style={{ maxWidth: 120 }}>
                  <Typo type="label3" color={color.gray500}>
                    {compositedFeedback?.customer_info?.name ||
                      CUSTOMER_ANONYMOUS}
                  </Typo>
                </EllipsisDiv>
              )}
            </Flex>
          </Flex>
          <Flex
            ref={titleRef}
            className="summary"
            onMouseEnter={(e) => {
              e.pageX;
              isEllipsisActive && setDisplayFullTitle(true);
            }}
            onMouseLeave={() => isEllipsisActive && setDisplayFullTitle(false)}
          >
            <EllipsisDiv>{displayingTitle}</EllipsisDiv>
          </Flex>
          {feedback.title && displayFullTitle && (
            <FullTitlePreviewStyle style={fixedPos} status={true}>
              {feedback.title}
            </FullTitlePreviewStyle>
          )}
        </Style>
      )}
    </Wrapper>
  );
};

export default forwardRef(FeedbackShortItem);

const Wrapper = styled(Flex)`
  width: 100%;
`;

const Style = styled(VFlex)<StateProps>`
  position: relative;
  width: 100%;
  padding: 10px 16px;
  transition: all 0.2s;
  cursor: pointer;
  border-radius: 8px;
  border-width: 1px;
  border-style: solid;
  background-color: transparent;
  opacity: 1;
  border-color: ${(props) => props.theme.colors.gray300};

  &:hover {
    background-color: ${(props) => props.theme.colors.hover};
  }
  .unread-mark {
    width: 12px;
  }
  .summary {
    width: 100%;
    color: ${(props) => props.theme.colors.gray700};
  }
  ${({ selected, theme, labeled, deleted }) => css`
    border-color: ${labeled && theme.colors.primary100};
    opacity: ${deleted && 0.6};
    ${selected &&
    css`
      background-color: ${theme.colors.selected};
      &:hover {
        background-color: ${(props) => props.theme.colors.selected};
      }
    `}
  `}
`;

const LabelStyle = styled(Flex)`
  position: absolute;
  right: 8px;
  top: 6px;
  gap: 6px;
  padding: 8px 12px;
`;
